import { useQuery } from '@tanstack/react-query';
import Card from '../../components/common/Card';
import BeltBadge from '../../components/common/BeltBadge';
import { MdEmojiEvents, MdCheckCircle, MdRadioButtonUnchecked, MdStar } from 'react-icons/md';
import api from '../../services/api';
import { useCalendar } from '../../context/CalendarContext';
import DualDate from '../../components/common/DualDate';
import { BELT_ORDER, getNextBelt } from '../../constants/belts';

const BELT_REQUIREMENTS = {
  'White-Yellow Stripe': ['Complete 10 training sessions', 'Learn basic stances (Charyot, Cheonbi)', 'Practice 3 basic kicks'],
  Yellow:              ['Complete 20 sessions', 'Demonstrate Taegeuk Il Jang poomsae', 'Pass sparring fundamentals'],
  'Yellow-Green Stripe':['Complete 30 sessions', 'Refine Taegeuk Il Jang', 'Demonstrate basic self-defence'],
  Green:               ['Complete 45 sessions', 'Demonstrate Taegeuk Sam Jang', 'Pass intermediate sparring'],
  'Green-Blue Stripe': ['Complete 60 sessions', 'Refine Taegeuk Sam Jang', 'Show consistent attendance'],
  Blue:                ['Complete 75 sessions', 'Demonstrate Taegeuk Sa Jang', 'Compete in at least 1 tournament'],
  'Blue-Red Stripe':   ['Complete 90 sessions', 'Refine Taegeuk Sa Jang', 'Show leadership to junior students'],
  Red:                 ['Complete 110 sessions', 'Demonstrate Taegeuk O Jang', 'Win or place in a regional competition'],
  'Red-Black Stripe':  ['Complete 140 sessions', 'Demonstrate Taegeuk Yuk Jang', 'Assist teaching junior classes (4+ sessions)'],
  'Black - 1st Dan':   ['Complete 200+ sessions', 'Demonstrate all Taegeuk poomsaes', 'Pass comprehensive theory exam', 'Show mastery in sparring and board breaking'],
  'Black - 2nd Dan':   ['Complete 300+ sessions', 'Demonstrate all poomsaes fluently', 'Pass Dan-level theory exam', 'Instructor recommendation required'],
  'Black - 3rd Dan':   ['Complete 400+ sessions', 'Master-level poomsae assessment', 'Significant contribution to the sport'],
};

function BeltProgress() {
  document.title = 'Belt Progress — TrainAxis';
  const { formatDate } = useCalendar();

  const { data: profileData, isPending } = useQuery({
    queryKey: ['student-me'],
    queryFn:  () => api.get('/api/students/me').then(r => r.data),
  });

  const { data: examsData } = useQuery({
    queryKey: ['my-upcoming-exams'],
    queryFn:  () => api.get('/api/belt-exams/my-upcoming').then(r => r.data),
  });

  if (isPending) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-36 bg-gray-100 animate-pulse rounded-2xl" />
        ))}
      </div>
    );
  }

  const profile     = profileData?.profile;
  const current     = profile?.currentGrade || 'White';
  const currentIdx  = BELT_ORDER.indexOf(current);
  const nextBelt    = getNextBelt(current);
  const progressPct = Math.round(((currentIdx + 1) / BELT_ORDER.length) * 100);
  const requirements = BELT_REQUIREMENTS[nextBelt] || [];
  const beltHistory  = profile?.beltHistory || [];
  const allAttempts  = profile?.gradingHistory || [];

  const upcomingExam = (examsData?.exams || []).find(e =>
    new Date(e.date) >= new Date() && ['scheduled', 'in_progress'].includes(e.status)
  );

  const isTopBelt = currentIdx === BELT_ORDER.length - 1;

  return (
    <div className="space-y-4">

      {/* Current belt hero */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <BeltBadge belt={current} size="xl" />
            <p className="text-xs text-gray-400">Current Belt</p>
          </div>

          <div className="flex-1 w-full">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-secondary">Journey Progress</p>
              <p className="text-sm font-bold text-primary">{progressPct}%</p>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3">
              <div
                className="h-3 rounded-full bg-primary transition-all duration-700"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-400">White</span>
              <span className="text-xs text-gray-400">Black - 3rd Dan</span>
            </div>

            {/* Belt milestone dots */}
            <div className="flex gap-0.5 mt-3">
              {BELT_ORDER.map((b, i) => (
                <div
                  key={b}
                  title={b}
                  className={`flex-1 h-2 rounded-full ${
                    i < currentIdx  ? 'bg-primary' :
                    i === currentIdx ? 'bg-primary ring-2 ring-primary/30 ring-offset-1' :
                    'bg-gray-100'
                  }`}
                />
              ))}
            </div>
          </div>

          {nextBelt && (
            <div className="flex flex-col items-center gap-3">
              <BeltBadge belt={nextBelt} size="xl" />
              <p className="text-xs text-gray-400">Next Belt</p>
            </div>
          )}
        </div>
      </Card>

      {/* Upcoming exam notice */}
      {upcomingExam && (
        <div className="bg-yellow-50 border border-yellow-100 rounded-2xl p-4 flex items-center gap-3">
          <MdEmojiEvents className="text-2xl text-yellow-500 flex-shrink-0" />
          <div>
            <p className="font-semibold text-secondary text-sm">Exam Scheduled</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {upcomingExam.title} · <DualDate date={upcomingExam.date} />
              {upcomingExam.venue ? ` · ${upcomingExam.venue}` : ''}
            </p>
          </div>
        </div>
      )}

      {/* Requirements for next belt */}
      {nextBelt && requirements.length > 0 && (
        <Card>
          <h3 className="font-semibold text-secondary text-sm mb-4 flex items-center gap-2">
            <MdEmojiEvents className="text-primary" />
            Requirements for {nextBelt}
          </h3>
          <div className="space-y-2.5">
            {requirements.map((req, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <MdRadioButtonUnchecked className="text-gray-300 text-lg flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600">{req}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4 italic">
            Ask your trainer for a belt exam recommendation when you feel ready.
          </p>
        </Card>
      )}

      {/* Top belt banner */}
      {isTopBelt && (
        <div className="bg-gray-900 text-white rounded-2xl p-5 text-center">
          <MdEmojiEvents className="text-4xl text-yellow-400 mx-auto mb-2" />
          <p className="font-bold text-lg">Black Belt — 3rd Dan</p>
          <p className="text-sm text-gray-400 mt-1">You have reached the highest rank. Continue your journey as a master and mentor.</p>
        </div>
      )}

      {/* Belt achievement history (promotions only) */}
      <Card>
        <h3 className="font-semibold text-secondary text-sm mb-4 flex items-center gap-2">
          <MdStar className="text-yellow-500" />
          Belt Achievements
        </h3>
        {!beltHistory.length ? (
          <div className="flex flex-col items-center py-8 text-center">
            <MdEmojiEvents className="text-4xl text-gray-200 mb-2" />
            <p className="text-gray-400 text-sm">No belt promotions yet</p>
            <p className="text-xs text-gray-300 mt-1">Your earned belts will appear here after exam finalization</p>
          </div>
        ) : (
          <div className="space-y-3">
            {[...beltHistory].reverse().map((b, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <BeltBadge belt={b.belt} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-secondary">{b.examTitle || 'Belt Exam'}</p>
                  <p className="text-xs text-gray-400"><DualDate date={b.achievedAt} /></p>
                </div>
                {b.overallScore != null && (
                  <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                    {b.overallScore}/10
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Full grading history (all attempts) */}
      <Card>
        <h3 className="font-semibold text-secondary text-sm mb-4">Grading History</h3>
        {!allAttempts.length ? (
          <div className="flex flex-col items-center py-8 text-center">
            <MdEmojiEvents className="text-4xl text-gray-200 mb-2" />
            <p className="text-gray-400 text-sm">No grading history yet</p>
          </div>
        ) : (
          <div className="relative pl-5">
            <div className="absolute left-0 top-1 bottom-1 w-0.5 bg-gray-100" />
            {[...allAttempts].reverse().map((g, i) => {
              const isManual = g.source === 'manual';
              return (
              <div key={i} className="relative mb-5 last:mb-0">
                <div className={`absolute -left-2.5 w-4 h-4 rounded-full border-2 border-white shadow-sm flex items-center justify-center ${isManual ? 'bg-blue-400' : g.passed ? 'bg-green-500' : 'bg-red-400'}`}>
                  {isManual
                    ? <MdStar className="text-white text-xs" />
                    : g.passed
                      ? <MdCheckCircle className="text-white text-xs" />
                      : <span className="text-white text-xs font-bold">×</span>
                  }
                </div>
                <div className="ml-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <BeltBadge belt={g.fromGrade} size="xs" />
                    <span className="text-xs text-gray-400">→</span>
                    <BeltBadge belt={g.toGrade} size="xs" />
                    {isManual ? (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        Manual correction
                      </span>
                    ) : (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${g.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {g.passed ? 'Passed' : 'Did not pass'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    <DualDate date={g.date} />{g.venue ? ` · ${g.venue}` : ''}
                  </p>
                  {g.notes && <p className="text-xs text-gray-500 mt-0.5 italic">"{g.notes}"</p>}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

export default BeltProgress;
